import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  fetchAirLogiExtensionId,
  fetchInventory,
  importAirLogiInventoryCsv,
  importProductsCsv,
  setCoefficient,
  setSafetyThreshold,
  syncAirLogiInventoryFromBridge,
  type AirLogiImportResult,
  type CsvImportResult,
  type InventoryResponse,
  type SyncLog,
} from "@/api/admin";
import { apiErrorMessage } from "@/api/client";
import {
  bridgeErrorToMessage,
  fetchStockInfo,
} from "@/utils/airlogi-bridge";
import { formatDateTime } from "@/utils/format";

export function AdminInventoryPage() {
  const [data, setData] = useState<InventoryResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [lastSync, setLastSync] = useState<SyncLog | null>(null);

  // Air-Logi 在庫 CSV 取込
  const [airLogiUploading, setAirLogiUploading] = useState(false);
  const [airLogiResult, setAirLogiResult] = useState<AirLogiImportResult | null>(null);
  const airLogiFileRef = useRef<HTMLInputElement>(null);

  // airlogi-bridge (Chrome 拡張) 経由の取得
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [bridgeRunning, setBridgeRunning] = useState(false);
  const [bridgeStep, setBridgeStep] = useState<string | null>(null);

  const [coefInput, setCoefInput] = useState("");
  const [savingCoef, setSavingCoef] = useState(false);

  const [thresholdInput, setThresholdInput] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);

  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<CsvImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchInventory(page, 50);
      setData(res);
      setLastSync(res.last_sync);
      if (coefInput === "") setCoefInput(String(res.coefficient));
      if (thresholdInput === "") setThresholdInput(String(res.safety_threshold));
    } catch (e) {
      setError(apiErrorMessage(e, "在庫一覧の取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  }, [page, coefInput, thresholdInput]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // 拡張 ID を 1 回だけ取得
  useEffect(() => {
    let cancelled = false;
    void fetchAirLogiExtensionId()
      .then((info) => {
        if (!cancelled) setExtensionId(info.extension_id);
      })
      .catch(() => {
        if (!cancelled) setExtensionId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** 拡張経由で Air-Logi から在庫情報を取得 → backend に POST → 結果表示 */
  const onBridgeSync = async () => {
    if (!extensionId) {
      setFlash({
        type: "err",
        text: "airlogi-bridge の拡張 ID が backend (.env) に未設定です。",
      });
      return;
    }
    setBridgeRunning(true);
    setAirLogiResult(null);
    setFlash(null);
    try {
      // Step 1: 拡張に getStockInfo を依頼
      setBridgeStep("Air-Logi にアクセスして在庫情報を取得中...");
      const bridgeRes = await fetchStockInfo(extensionId);
      if (!bridgeRes.ok) {
        setFlash({ type: "err", text: bridgeErrorToMessage(bridgeRes) });
        return;
      }
      const rows = bridgeRes.data.rows;
      if (!rows.length) {
        setFlash({
          type: "err",
          text: "Air-Logi から在庫情報が 0 件返ってきました。",
        });
        return;
      }

      // Step 2: backend に POST して DB 反映
      setBridgeStep(`${rows.length} 件のデータを反映中...`);
      const result = await syncAirLogiInventoryFromBridge(rows);
      setAirLogiResult(result);
      setLastSync(result.sync_log);
      setFlash({
        type: "ok",
        text: `取込完了: ${result.updated} 件を更新、${result.skipped} 件をスキップ (Air-Logi 取得 ${rows.length} 件)`,
      });
      await load();
    } catch (e) {
      setFlash({
        type: "err",
        text: apiErrorMessage(e, "拡張経由の取込に失敗しました"),
      });
    } finally {
      setBridgeRunning(false);
      setBridgeStep(null);
    }
  };

  const onAirLogiImport = async (e: FormEvent) => {
    e.preventDefault();
    const file = airLogiFileRef.current?.files?.[0];
    if (!file) {
      setFlash({ type: "err", text: "在庫 CSV を選択してください" });
      return;
    }
    setAirLogiUploading(true);
    setAirLogiResult(null);
    setFlash(null);
    try {
      const res = await importAirLogiInventoryCsv(file);
      setAirLogiResult(res);
      setLastSync(res.sync_log);
      setFlash({
        type: "ok",
        text: `取込完了: ${res.updated} 件を更新、${res.skipped} 件をスキップ`,
      });
      if (airLogiFileRef.current) airLogiFileRef.current.value = "";
      await load();
    } catch (err) {
      setFlash({
        type: "err",
        text: apiErrorMessage(err, "在庫 CSV の取込に失敗しました"),
      });
    } finally {
      setAirLogiUploading(false);
    }
  };

  const onSaveCoefficient = async (e: FormEvent) => {
    e.preventDefault();
    const value = Number(coefInput);
    if (Number.isNaN(value) || value <= 0 || value > 1) {
      setFlash({ type: "err", text: "係数は 0 より大きく 1 以下の数値で入力してください" });
      return;
    }
    setSavingCoef(true);
    setFlash(null);
    try {
      const res = await setCoefficient(value);
      setFlash({
        type: "ok",
        text: `係数を ${res.coefficient} に変更。${res.products_recalculated} 件の公開在庫を再計算しました`,
      });
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "係数変更に失敗しました") });
    } finally {
      setSavingCoef(false);
    }
  };

  const onSaveThreshold = async (e: FormEvent) => {
    e.preventDefault();
    const value = Number(thresholdInput);
    if (!Number.isInteger(value) || value < 0 || value > 10000) {
      setFlash({
        type: "err",
        text: "足切り閾値は 0〜10000 の整数で入力してください（0 で足切り無効）",
      });
      return;
    }
    setSavingThreshold(true);
    setFlash(null);
    try {
      const res = await setSafetyThreshold(value);
      setFlash({
        type: "ok",
        text:
          value === 0
            ? `足切りを無効にしました。${res.products_recalculated} 件の公開在庫を再計算しました`
            : `足切り閾値を ${res.safety_threshold} に変更。実在庫が${res.safety_threshold}以下の商品は公開在庫が0になります（${res.products_recalculated} 件を再計算）`,
      });
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "閾値変更に失敗しました") });
    } finally {
      setSavingThreshold(false);
    }
  };

  const onCsvSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setFlash({ type: "err", text: "CSV ファイルを選択してください" });
      return;
    }
    setCsvUploading(true);
    setCsvResult(null);
    setFlash(null);
    try {
      const res = await importProductsCsv(file);
      setCsvResult(res);
      setFlash({
        type: "ok",
        text: `インポート完了: 新規 ${res.imported} 件 / 更新 ${res.updated} 件 / スキップ ${res.skipped} 件`,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "CSVインポートに失敗しました") });
    } finally {
      setCsvUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">在庫・商品管理</h1>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <form onSubmit={onAirLogiImport} className="card p-4">
          <h2 className="font-semibold mb-2">Air-Logi 在庫取込</h2>
          <p className="text-xs text-gray-500 mb-2">
            airlogi-bridge 拡張経由で自動取得するか、CSV を手動アップロードします。
            品番・バーコードで JAN 照合し、未登録はスキップします。
          </p>
          <div className="text-xs text-gray-600 mb-2">
            前回:{" "}
            {lastSync
              ? `${formatDateTime(lastSync.started_at)} (${lastSync.status}) / 更新 ${lastSync.products_updated} 件`
              : "未実行"}
          </div>

          {/* 拡張経由で取得 (推奨) */}
          <button
            type="button"
            onClick={() => void onBridgeSync()}
            disabled={bridgeRunning || airLogiUploading || !extensionId}
            className="btn-primary w-full mb-2"
            title={
              extensionId
                ? "Chrome 拡張 airlogi-bridge を経由して Air-Logi から取得"
                : "backend (.env) に AIRLOGI_BRIDGE_EXTENSION_ID を設定してください"
            }
          >
            {bridgeRunning ? bridgeStep || "拡張経由で取得中..." : "拡張経由で取得"}
          </button>
          {!extensionId && (
            <p className="text-xs text-red-600 mb-2">
              拡張 ID が未設定です（backend の .env に AIRLOGI_BRIDGE_EXTENSION_ID を設定）。
            </p>
          )}

          {/* 手動 CSV アップロード (フォールバック) */}
          <details>
            <summary className="text-xs text-gray-500 cursor-pointer underline">
              CSV を手動アップロード（拡張が動かない時のフォールバック）
            </summary>
            <div className="mt-2">
              <input
                ref={airLogiFileRef}
                type="file"
                accept=".csv,text/csv"
                className="block w-full text-sm mb-2"
              />
              <button
                type="submit"
                className="btn-secondary w-full"
                disabled={airLogiUploading || bridgeRunning}
              >
                {airLogiUploading ? "取込中..." : "CSV を取込"}
              </button>
            </div>
          </details>
        </form>

        <form onSubmit={onSaveCoefficient} className="card p-4">
          <h2 className="font-semibold mb-2">公開係数</h2>
          <p className="text-xs text-gray-500 mb-3">
            公開在庫 = floor(実在庫 × 係数) − 引当数。0-1 の小数で入力。
          </p>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              type="number"
              step="0.01"
              min="0.01"
              max="1"
              value={coefInput}
              onChange={(e) => setCoefInput(e.target.value)}
            />
            <button type="submit" className="btn-primary" disabled={savingCoef}>
              {savingCoef ? "保存中..." : "保存"}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500">現在値: {data?.coefficient ?? "-"}</div>
        </form>

        <form onSubmit={onSaveThreshold} className="card p-4">
          <h2 className="font-semibold mb-2">在庫足切り閾値</h2>
          <p className="text-xs text-gray-500 mb-3">
            実在庫がこの数値以下の商品は、公開在庫を自動的に 0 にします。0 で無効化。
          </p>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              type="number"
              step="1"
              min="0"
              max="10000"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
            />
            <button type="submit" className="btn-primary" disabled={savingThreshold}>
              {savingThreshold ? "保存中..." : "保存"}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            現在値: {data?.safety_threshold ?? "-"}
            {data?.safety_threshold === 0 && (
              <span className="ml-2 badge-gray">無効</span>
            )}
          </div>
        </form>

        <form onSubmit={onCsvSubmit} className="card p-4">
          <h2 className="font-semibold mb-2">商品 CSV インポート</h2>
          <p className="text-xs text-gray-500 mb-2">
            JAN をキーに UPSERT（既存なら更新・新規なら登録）。UTF-8 または Shift_JIS 対応。
          </p>
          <details className="text-xs text-gray-600 mb-2">
            <summary className="cursor-pointer underline">CSV 項目一覧（クリックで展開）</summary>
            <div className="mt-1 space-y-1">
              <div>
                <span className="font-semibold">必須列 (5):</span>{" "}
                <code>jan</code> / <code>product_name</code> / <code>unit_price</code>（円・整数）/{" "}
                <code>order_unit</code>（発注単位・1以上）/ <code>tier</code>（1,2,3）
              </div>
              <div>
                <span className="font-semibold">任意列 (7):</span>{" "}
                <code>category</code> / <code>brand</code> / <code>image_url</code> /{" "}
                <code>notes</code> / <code>custom_field_1</code>〜<code>3</code>
              </div>
              <div className="text-gray-500">
                ※ 在庫数（airlogi_stock/public_stock/allocated_stock）はシステム管理のため CSV 不要。
                Air-Logi 連携で自動更新されます。
              </div>
            </div>
          </details>
          <a
            href="/product_import_template.csv"
            download
            className="text-xs text-brand-700 hover:underline block mb-2"
          >
            ⬇ テンプレート CSV をダウンロード
          </a>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="block w-full text-sm mb-2"
          />
          <button type="submit" className="btn-primary w-full" disabled={csvUploading}>
            {csvUploading ? "アップロード中..." : "CSV をアップロード"}
          </button>
        </form>
      </div>

      {airLogiResult && airLogiResult.skipped_jans.length > 0 && (
        <div className="card p-4 border-yellow-300">
          <h3 className="font-semibold mb-2">
            スキップされた JAN ({airLogiResult.skipped_jans.length}件)
          </h3>
          <p className="text-xs text-gray-600 mb-2">
            商品マスタに未登録、または在庫数が数値でないため取り込まれませんでした。
            先に商品 CSV インポートで商品マスタに登録してください。
          </p>
          <div className="max-h-48 overflow-auto text-xs font-mono">
            {airLogiResult.skipped_jans.map((jan) => (
              <div key={jan}>{jan}</div>
            ))}
          </div>
        </div>
      )}

      {csvResult && csvResult.errors.length > 0 && (
        <div className="card p-4 border-red-300">
          <h3 className="font-semibold mb-2">エラー行 ({csvResult.errors.length}件)</h3>
          <div className="max-h-48 overflow-auto text-xs">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-2 py-1 text-left">行</th>
                  <th className="px-2 py-1 text-left">JAN</th>
                  <th className="px-2 py-1 text-left">理由</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {csvResult.errors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-1">{err.row}</td>
                    <td className="px-2 py-1 font-mono">{err.jan ?? "-"}</td>
                    <td className="px-2 py-1">{err.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-semibold mb-2">在庫一覧</h2>
        {loading ? (
          <div className="text-center py-12 text-gray-500">読み込み中...</div>
        ) : !data || data.inventory.length === 0 ? (
          <div className="card p-8 text-center text-gray-500">商品がありません。</div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">JAN</th>
                  <th className="px-3 py-2 text-left font-medium">商品名</th>
                  <th className="px-3 py-2 text-right font-medium">実在庫</th>
                  <th className="px-3 py-2 text-right font-medium">引当</th>
                  <th className="px-3 py-2 text-right font-medium">公開在庫</th>
                  <th className="px-3 py-2 text-center font-medium">有効</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.inventory.map((row) => (
                  <tr key={row.product_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{row.jan}</td>
                    <td className="px-3 py-2">{row.product_name}</td>
                    <td className="px-3 py-2 text-right">{row.airlogi_stock}</td>
                    <td className="px-3 py-2 text-right">{row.allocated_stock}</td>
                    <td className="px-3 py-2 text-right font-medium">{row.public_stock}</td>
                    <td className="px-3 py-2 text-center">
                      {row.is_active ? (
                        <span className="badge-green">有効</span>
                      ) : (
                        <span className="badge-gray">無効</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              前へ
            </button>
            <div className="text-sm">
              {data.pagination.page} / {data.pagination.total_pages}（{data.pagination.total} 件）
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((p) => Math.min(data.pagination.total_pages, p + 1))}
              disabled={page >= data.pagination.total_pages}
            >
              次へ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
